/**
 * DisclosureFieldsForm
 * Generic form component that fetches field metadata for a given disclosure
 * and renders a dynamic form driven entirely by the fields array from the API.
 *
 * Replaces per-type disclosure form components (CoRadonForm, WaMoldForm, etc.)
 * with a single data-driven implementation.
 */

import { useState, useEffect, useCallback } from 'react';
import type { DisclosureField, DisclosureFieldsResponse } from '../../types/lease-generation';
import { fetchDisclosureFields } from '../../lib/api';

interface DisclosureFieldsFormProps {
  disclosureId: string;
  unitId: string;
  /** Current field values */
  value: Record<string, unknown>;
  /** Called with the merged values object whenever a field changes */
  onChange: (values: Record<string, unknown>) => void;
  disabled?: boolean;
}

/** Skeleton placeholder shown while fields are loading */
function LoadingSkeleton({ disclosureId }: { disclosureId: string }) {
  return (
    <div
      aria-busy="true"
      aria-label="Loading disclosure fields"
      data-testid={`disclosure-fields-form-${disclosureId}`}
      className="flex flex-col gap-4"
    >
      {[1, 2, 3].map(i => (
        <div key={i} className="flex flex-col gap-1">
          <div className="h-4 w-32 rounded bg-accent animate-pulse" />
          <div className="h-[44px] w-full rounded-md bg-accent animate-pulse" />
        </div>
      ))}
    </div>
  );
}

/** Decimal input using text+inputMode so mid-typing states ("4.", "4.0") aren't coerced.
 *  Converts to number only on blur. */
function NumberFieldInput({
  inputId, testId, value, onChange, disabled, required, nullable, baseInputClass,
}: {
  inputId: string; testId: string; value: unknown; onChange: (v: number | null) => void;
  disabled?: boolean; required?: boolean; nullable?: boolean; baseInputClass: string;
}) {
  const [display, setDisplay] = useState(value != null ? String(value) : '');
  useEffect(() => { setDisplay(value != null ? String(value) : ''); }, [value]);
  return (
    <input
      id={inputId} type="text" inputMode="decimal" data-testid={testId}
      value={display} onChange={e => setDisplay(e.target.value)}
      onBlur={e => { const raw = e.target.value; onChange(raw === '' ? null : Number(raw)); }}
      disabled={disabled} aria-required={required ? 'true' : undefined}
      placeholder={nullable ? 'Leave blank if not applicable' : ''} className={baseInputClass}
    />
  );
}

/** Renders a single disclosure field with the appropriate input type */
function DisclosureFieldInput({
  field,
  disclosureId,
  value,
  onChange,
  disabled,
}: {
  field: DisclosureField;
  disclosureId: string;
  value: unknown;
  onChange: (newValue: unknown) => void;
  disabled?: boolean;
}) {
  const inputId = `disclosure-field-${disclosureId}-${field.name}-input`;
  const testId = `disclosure-field-${disclosureId}-${field.name}`;
  const baseInputClass =
    'w-full px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed';

  const fieldType = field.type;

  if (fieldType === 'boolean') {
    const boolValue = value === true || value === 'true' ? true : value === false || value === 'false' ? false : undefined;
    return (
      <fieldset
        data-testid={testId}
        aria-required={field.required ? 'true' : undefined}
        className="flex flex-col gap-1"
      >
        <legend className="text-sm font-medium text-foreground">
          {field.label}
          {field.required && <span className="text-destructive ml-0.5">*</span>}
        </legend>
        <div className="flex gap-6 min-h-[44px] items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              id={`${inputId}-yes`}
              type="radio"
              name={`${disclosureId}-${field.name}`}
              value="yes"
              data-testid={`${testId}-yes`}
              checked={boolValue === true}
              onChange={() => onChange(true)}
              disabled={disabled}
              className="h-4 w-4 border-input text-primary focus:ring-ring cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-sm text-foreground">Yes</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              id={`${inputId}-no`}
              type="radio"
              name={`${disclosureId}-${field.name}`}
              value="no"
              data-testid={`${testId}-no`}
              checked={boolValue === false}
              onChange={() => onChange(false)}
              disabled={disabled}
              className="h-4 w-4 border-input text-primary focus:ring-ring cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-sm text-foreground">No</span>
          </label>
        </div>
      </fieldset>
    );
  }

  if (fieldType === 'select') {
    const currentVal = value != null ? String(value) : '';
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={inputId} className="block text-sm font-medium text-foreground">
          {field.label}
          {field.required && <span className="text-destructive ml-0.5">*</span>}
        </label>
        <select
          id={inputId}
          data-testid={testId}
          value={currentVal}
          onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
          disabled={disabled}
          aria-required={field.required ? 'true' : undefined}
          className={baseInputClass}
        >
          {(!field.required || field.nullable) && (
            <option value="">— None / Not applicable —</option>
          )}
          {(field.options ?? []).map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (fieldType === 'number') {
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={inputId} className="block text-sm font-medium text-foreground">
          {field.label}
          {field.required && <span className="text-destructive ml-0.5">*</span>}
        </label>
        <NumberFieldInput
          inputId={inputId} testId={testId} value={value} onChange={onChange}
          disabled={disabled} required={field.required} nullable={field.nullable}
          baseInputClass={baseInputClass}
        />
      </div>
    );
  }

  if (fieldType === 'date') {
    const dateVal = value != null ? String(value) : '';
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={inputId} className="block text-sm font-medium text-foreground">
          {field.label}
          {field.required && <span className="text-destructive ml-0.5">*</span>}
        </label>
        <input
          id={inputId}
          type="date"
          data-testid={testId}
          value={dateVal}
          onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
          disabled={disabled}
          aria-required={field.required ? 'true' : undefined}
          className={baseInputClass}
        />
      </div>
    );
  }

  // 'text' and any unknown types — fall back to text input (do not crash)
  const textVal = value != null ? String(value) : '';
  const isLongText = textVal.length > 100;
  if (isLongText) {
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={inputId} className="block text-sm font-medium text-foreground">
          {field.label}
          {field.required && <span className="text-destructive ml-0.5">*</span>}
        </label>
        <textarea
          id={inputId}
          data-testid={testId}
          value={textVal}
          onChange={e => onChange(e.target.value === '' ? (field.nullable ? null : '') : e.target.value)}
          disabled={disabled}
          aria-required={field.required ? 'true' : undefined}
          rows={4}
          className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed resize-y"
        />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="block text-sm font-medium text-foreground">
        {field.label}
        {field.required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      <input
        id={inputId}
        type="text"
        data-testid={testId}
        value={textVal}
        onChange={e => onChange(e.target.value === '' ? (field.nullable ? null : '') : e.target.value)}
        disabled={disabled}
        aria-required={field.required ? 'true' : undefined}
        placeholder={field.nullable ? 'Leave blank if not applicable' : ''}
        className={baseInputClass}
      />
    </div>
  );
}

export function DisclosureFieldsForm({
  disclosureId,
  unitId,
  value,
  onChange,
  disabled,
}: DisclosureFieldsFormProps) {
  const [fieldsData, setFieldsData] = useState<DisclosureFieldsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFields = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchDisclosureFields(disclosureId, unitId);
      setFieldsData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load disclosure fields');
    } finally {
      setLoading(false);
    }
  }, [disclosureId, unitId]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  // Seed parent formData with defaults for any fields not yet present.
  // Required selects are seeded to their first option so the browser's
  // rendered value matches saved state. Boolean fields are intentionally
  // NOT seeded — they must remain undefined until the user picks Yes or No.
  useEffect(() => {
    if (!fieldsData) return;
    const missing: Record<string, unknown> = {};
    let needsUpdate = false;
    for (const field of fieldsData.fields) {
      if (!(field.name in value)) {
        needsUpdate = true;
        if (field.type === 'select' && field.required && !field.nullable) {
          // Browser shows first option for a controlled select with no matching value;
          // seed state to match so it's treated as a valid selection.
          missing[field.name] = field.options?.[0]?.value ?? null;
        } else {
          missing[field.name] = null;
        }
      }
    }
    if (needsUpdate) {
      onChange({ ...value, ...missing });
    }
    // Intentionally omit `value` — this is a one-time seed when fields load,
    // not a continuous sync. Including `value` would cause infinite loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsData, onChange]);

  if (loading) {
    return <LoadingSkeleton disclosureId={disclosureId} />;
  }

  if (error) {
    return (
      <div
        data-testid={`disclosure-fields-form-${disclosureId}`}
        className="flex flex-col gap-3 p-4 rounded-lg border border-destructive/30 bg-destructive/5"
        role="alert"
      >
        <p className="text-sm text-destructive">{error}</p>
        <button
          type="button"
          onClick={loadFields}
          className="self-start text-sm font-medium text-primary hover:text-primary/80 underline"
          data-testid={`disclosure-fields-retry-${disclosureId}`}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!fieldsData) {
    return null;
  }

  const { fields } = fieldsData;

  if (fields.length === 0) {
    return (
      <div
        data-testid={`disclosure-fields-form-${disclosureId}`}
        className="flex items-center gap-2 p-4 rounded-lg bg-accent/50 border border-border"
        role="form"
        aria-label={`Disclosure fields for ${disclosureId}`}
      >
        <p className="text-sm text-muted-foreground">
          No additional information required for this disclosure.
        </p>
      </div>
    );
  }

  const handleFieldChange = (fieldName: string, newValue: unknown) => {
    onChange({ ...value, [fieldName]: newValue });
  };

  return (
    <form
      data-testid={`disclosure-fields-form-${disclosureId}`}
      aria-label={`Disclosure fields for ${disclosureId}`}
      className="flex flex-col gap-4"
      onSubmit={e => e.preventDefault()}
    >
      {fields.map(field => (
        <DisclosureFieldInput
          key={field.name}
          field={field}
          disclosureId={disclosureId}
          value={value[field.name]}
          onChange={newVal => handleFieldChange(field.name, newVal)}
          disabled={disabled}
        />
      ))}
    </form>
  );
}
